import * as React from 'react';
import {
  useForm,
  UseFormReturn,
  FormProvider,
  FieldValues,
  FieldErrors,
  Resolver,
  DefaultValues,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useUnsavedChanges } from '../hooks/use-unsaved-changes';
import { logger } from '../utils/logger';

import { Box } from './box';
import { VStack, HStack } from './stack';
import { Progress } from './progress';
import { Text } from './text';
import { Container } from './container';
import { Card } from './card';
import { SaveIndicator, SaveStatus } from './validation/save-indicator';
import { ErrorSummary } from './validation/error-summary';
import { Button } from './button';
import { toaster } from './ui/toaster';

// Core types and interfaces
export interface StepConfig<T extends FieldValues = FieldValues> {
  id: string;
  title: string;
  description?: string;
  schema: z.ZodType<unknown>;
  component: React.ComponentType<StepComponentProps<T>>;
  isOptional?: boolean;
  condition?: (data: Partial<T>) => boolean;
}

export interface StepComponentProps<T extends FieldValues = FieldValues> {
  formMethods: UseFormReturn<T>;
  currentStepData: Partial<T>;
  allFormData: Partial<T>;
  isActive: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
}

export interface MultiStepFormProps<T extends FieldValues = FieldValues> {
  steps: StepConfig<T>[];
  onSubmit: (data: T) => void | Promise<void>;
  onStepChange?: (stepIndex: number, stepId: string) => void;
  defaultValues?: Partial<T>;
  children?: React.ReactNode;
  className?: string;
  showProgress?: boolean;
  persistData?: boolean;
  persistKey?: string;
  autoSaveInterval?: number; // in milliseconds
  onAutoSave?: (data: Partial<T>) => Promise<void>;
  showSaveIndicator?: boolean;
  enableUnsavedWarning?: boolean;
  saveIndicatorPosition?: 'top' | 'bottom' | 'inline';
  showValidationSummary?: boolean;
  validationSummaryPosition?: 'top' | 'bottom';
  validationSummaryTitle?: string;
  // 🆕 NEW PROPS for unified form pattern
  mode?: 'auto' | 'single' | 'wizard'; // Auto-detect based on steps.length
  showStepper?: boolean; // Override stepper visibility (default: mode !== 'single')
  showNavigationButtons?: boolean; // Show Previous/Next/Submit buttons (default: true)
  submitButtonText?: string; // Customize submit button text
  previousButtonText?: string; // Customize previous button text
  nextButtonText?: string; // Customize next button text
  onCancel?: () => void; // Optional cancel handler
  cancelButtonText?: string; // Customize cancel button text
}

interface MultiStepFormState<T extends FieldValues = FieldValues> {
  currentStep: number;
  formData: Partial<T>;
  stepValidation: Record<string, boolean>;
  completedSteps: Set<number>;
}

// Session storage utilities for form persistence
const getStorageKey = (persistKey: string) => `multi-step-form-${persistKey}`;

const saveFormData = <T extends FieldValues>(persistKey: string, data: Partial<T>) => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(getStorageKey(persistKey), JSON.stringify(data));
    } catch (error) {
      logger.warn('Failed to save form data to session storage', { error, persistKey });
    }
  }
};

const loadFormData = <T extends FieldValues>(persistKey: string): Partial<T> | null => {
  if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(getStorageKey(persistKey));
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      logger.warn('Failed to load form data from session storage', { error, persistKey });
      return null;
    }
  }
  return null;
};

const clearFormData = (persistKey: string) => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(getStorageKey(persistKey));
    } catch (error) {
      logger.warn('Failed to clear form data from session storage', { error, persistKey });
    }
  }
};

// Context for step communication
interface MultiStepFormContextType<T extends FieldValues = FieldValues> {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  canGoPrevious: boolean;
  goToStep: (step: number) => void;
  goNext: () => void;
  goPrevious: () => void;
  getCurrentStepData: () => Partial<T>;
  getAllFormData: () => Partial<T>;
  updateFormData: (data: Partial<T>) => void;
  submitForm: () => void;
}

const MultiStepFormContext = React.createContext<MultiStepFormContextType<
  Record<string, unknown>
> | null>(null);

export const useMultiStepForm = <T extends FieldValues = FieldValues>() => {
  const context = React.useContext(MultiStepFormContext) as MultiStepFormContextType<T>;
  if (!context) {
    throw new Error('useMultiStepForm must be used within a MultiStepForm');
  }
  return context;
};

// Optional version that doesn't throw error
export const useMultiStepFormOptional = <T extends FieldValues = FieldValues>() => {
  const context = React.useContext(MultiStepFormContext) as MultiStepFormContextType<T>;
  return context;
};

/**
 * MultiStepForm - Unified form component supporting both single-step and multi-step wizards
 *
 * 🎯 **UNIFIED PATTERN**: Use this component for ALL forms in the project
 *
 * Features:
 * - Single-step forms (mode: 'single') and multi-step wizards (mode: 'wizard')
 * - Auto-detection based on steps.length (mode: 'auto')
 * - Built-in navigation buttons (Previous/Next/Submit/Cancel)
 * - Individual step validation with Zod schemas
 * - Form data persistence across steps
 * - Progress indicator with conditional visibility
 * - Conditional step rendering
 * - Session storage for data persistence
 * - Card wrapper styling (consistent UX)
 *
 * @example Single-Step Form
 * ```tsx
 * <MultiStepForm
 *   steps={[
 *     {
 *       id: 'vehicle-info',
 *       title: 'Vehicle Information',
 *       schema: vehicleSchema,
 *       component: VehicleFormStep
 *     }
 *   ]}
 *   onSubmit={handleSubmit}
 *   mode="single"  // or "auto" (default)
 *   submitButtonText="Save Vehicle"
 * />
 * ```
 *
 * @example Multi-Step Wizard
 * ```tsx
 * <MultiStepForm
 *   steps={[
 *     {
 *       id: 'personal',
 *       title: 'Personal Information',
 *       schema: z.object({ name: z.string().min(1) }),
 *       component: PersonalInfoStep
 *     },
 *     {
 *       id: 'contact',
 *       title: 'Contact Details',
 *       schema: z.object({ email: z.string().email() }),
 *       component: ContactStep
 *     }
 *   ]}
 *   onSubmit={handleSubmit}
 *   mode="wizard"  // or "auto" (default)
 *   persistData
 *   persistKey="customer-onboarding"
 * />
 * ```
 */
export const MultiStepForm = <T extends FieldValues = FieldValues>({
  steps,
  onSubmit,
  onStepChange,
  defaultValues = {},
  children,
  className,
  showProgress = true,
  persistData = false,
  persistKey = 'default',
  autoSaveInterval = 0, // 0 means disabled
  onAutoSave,
  showSaveIndicator = false,
  enableUnsavedWarning = false,
  saveIndicatorPosition = 'top',
  // Default ON — the existing render guard `getValidationErrors.length > 0`
  // means the summary is invisible while the form is valid (no UX surprise),
  // and visible the moment validation fails. Pass `false` explicitly to opt out
  // (e.g. forms with their own bespoke error rendering).
  showValidationSummary = true,
  validationSummaryPosition = 'top',
  validationSummaryTitle,
  // 🆕 NEW PROPS
  mode = 'auto',
  showStepper,
  showNavigationButtons = true,
  submitButtonText,
  previousButtonText = 'Previous',
  nextButtonText = 'Next',
  onCancel,
  cancelButtonText = 'Cancel',
}: MultiStepFormProps<T>) => {
  // Initialize form data with persistence support
  const initializeFormData = React.useCallback((): Partial<T> => {
    if (persistData) {
      const stored = loadFormData<T>(persistKey);
      if (stored) {
        return { ...defaultValues, ...stored };
      }
    }
    return defaultValues;
  }, [defaultValues, persistData, persistKey]);

  // State management
  const [state, setState] = React.useState<MultiStepFormState<T>>(() => ({
    currentStep: 0,
    formData: initializeFormData(),
    stepValidation: {},
    completedSteps: new Set(),
  }));

  // Ref to track latest formData — avoids stale closure in navigation callbacks.
  // Bug #49/#50: goToStep used state.formData from closure, missing data saved
  // by updateFormData in the same synchronous call stack.
  const formDataRef = React.useRef<Partial<T>>(state.formData);
  React.useEffect(() => {
    formDataRef.current = state.formData;
  }, [state.formData]);

  // Auto-save state
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = React.useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const autoSaveTimerRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const retryCountRef = React.useRef(0);
  const maxRetries = 3;

  // Create the combined schema for current step validation
  const currentStepSchema = React.useMemo(() => {
    const currentStepConfig = steps[state.currentStep];
    return currentStepConfig?.schema;
  }, [steps, state.currentStep]);

  // Initialize form with current step schema
  const formMethods = useForm<T>({
    // @ts-any-allowed: zodResolver type compatibility with dynamic schemas
    resolver: currentStepSchema
      ? (zodResolver as (schema: z.ZodType<unknown>) => Resolver<T>)(currentStepSchema)
      : undefined,
    defaultValues: state.formData as DefaultValues<T>,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setFocus,
    formState: { isValid, errors, isDirty: formIsDirty },
  } = formMethods;

  // Use unsaved changes hook for browser warning
  useUnsavedChanges({
    isDirty: enableUnsavedWarning && formIsDirty && !isSubmitting,
    isSubmitting,
    message: 'You have unsaved changes in your form. Are you sure you want to leave?',
  });

  // Format validation errors for summary
  const getValidationErrors = React.useMemo(() => {
    const errorList: Array<{ field: string; message: string; label?: string }> = [];

    // Recursively walk the FieldErrors tree. The old top-level pass only
    // matched flat schemas — nested object fields like
    // `pickupLocation: { address: {message}, city: {message} }` have no
    // `.message` on the parent and were silently skipped. That's the B-082
    // pattern: step 1 (flat `customerId`/`type`/`priority`) surfaced a
    // proper "Lütfen aşağıdaki hataları düzeltin:" panel; step 2
    // (Konumlar) used the nested `locationSchema` and rendered nothing,
    // making the İleri click feel like a silent no-op.
    const visit = (node: unknown, path: string): void => {
      if (!node || typeof node !== 'object') return;
      if ('message' in (node as object)) {
        const message = (node as { message?: unknown }).message;
        if (typeof message === 'string' && message.length > 0) {
          const leaf = path.split('.').pop() || path;
          errorList.push({
            field: path,
            message,
            label:
              leaf.charAt(0).toUpperCase() +
              leaf
                .slice(1)
                .replace(/([A-Z])/g, ' $1')
                .trim(),
          });
          return;
        }
      }
      Object.entries(node as Record<string, unknown>).forEach(([key, child]) => {
        // `ref` / `type` are RHF internals, not real subpaths.
        if (key === 'ref' || key === 'type') return;
        visit(child, path ? `${path}.${key}` : key);
      });
    };

    if (errors && Object.keys(errors).length > 0) {
      visit(errors as unknown, '');
    }

    return errorList;
  }, [errors]);

  // Handle field click in error summary
  const handleFieldClick = React.useCallback(
    (field: string) => {
      try {
        // Try to focus the field - may fail if field name doesn't exist
        // @ts-any-allowed: react-hook-form setFocus requires Path<T> type which is complex
        (setFocus as (name: string) => void)(field);
      } catch {
        // Field might not exist on current step, ignore
      }
      // Scroll to the field
      const element = document.querySelector(`[name="${field}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [setFocus]
  );

  // Surface validation failures so failed Next/Submit clicks aren't silent.
  // RHF's `handleSubmit(onValid, onInvalid)` calls this when zod validation
  // rejects the current step. Without it, clicking Next on an invalid form
  // does nothing visible — QA tickets B-001/B-014/T-012/T-019/T-035/T-046
  // were all variants of "Save button cevap vermiyor / form hangs". With
  // the focus + scroll, the user sees the input that needs fixing; the
  // existing `<ErrorSummary>` (now defaulted on) lists every error.
  const handleValidationFailure = React.useCallback(
    (validationErrors: FieldErrors<T>) => {
      if (!validationErrors || Object.keys(validationErrors).length === 0) return;
      // Walk to the first leaf to get the real dotted path so focus + scroll
      // land on the actual input (e.g. `pickupLocation.address`), not on a
      // non-existent `pickupLocation` element. Matches the recursion in
      // getValidationErrors above.
      const firstLeaf = ((): string | null => {
        const walk = (node: unknown, path: string): string | null => {
          if (!node || typeof node !== 'object') return null;
          if (
            'message' in (node as object) &&
            typeof (node as { message?: unknown }).message === 'string'
          ) {
            return path;
          }
          for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
            if (key === 'ref' || key === 'type') continue;
            const found = walk(child, path ? `${path}.${key}` : key);
            if (found) return found;
          }
          return null;
        };
        return walk(validationErrors, '');
      })();
      logger.warn('MultiStepForm validation blocked progression', {
        persistKey,
        step: state.currentStep,
        firstField: firstLeaf,
      });
      if (firstLeaf) handleFieldClick(firstLeaf);

      // ErrorSummary renders at the top of the form body, which is often
      // out of view when the user clicks Next/Submit at the bottom of a
      // scrollable Dialog (QA #161: "Hiç hata göstermiyor"). Toast the
      // first error so feedback is position-independent. Walk the same
      // nested-leaf path used for focus to surface the actual leaf message.
      const firstMessage = (() => {
        if (!firstLeaf) return undefined;
        const segments = firstLeaf.split('.');
        let node: unknown = validationErrors;
        for (const seg of segments) {
          if (node && typeof node === 'object' && seg in node) {
            node = (node as Record<string, unknown>)[seg];
          } else {
            return undefined;
          }
        }
        return node && typeof node === 'object' && 'message' in node
          ? ((node as { message?: string }).message as string | undefined)
          : undefined;
      })();
      toaster.error({
        title: validationSummaryTitle ?? 'Please correct the highlighted fields',
        description: firstMessage,
      });
    },
    [handleFieldClick, persistKey, state.currentStep, validationSummaryTitle]
  );

  // Filter steps based on conditions
  const visibleSteps = React.useMemo(() => {
    return steps.filter((step) => {
      if (!step.condition) return true;
      return step.condition(state.formData);
    });
  }, [steps, state.formData]);

  // 🆕 Mode detection logic
  const effectiveMode = React.useMemo(() => {
    if (mode !== 'auto') return mode;
    return visibleSteps.length === 1 ? 'single' : 'wizard';
  }, [mode, visibleSteps.length]);

  // 🆕 Determine stepper visibility
  const shouldShowStepper = React.useMemo(() => {
    if (showStepper !== undefined) return showStepper;
    return effectiveMode === 'wizard' && showProgress;
  }, [showStepper, effectiveMode, showProgress]);

  // 🆕 Button visibility logic
  const shouldShowPrevious = effectiveMode === 'wizard' && state.currentStep > 0;
  const shouldShowNext = effectiveMode === 'wizard' && state.currentStep < visibleSteps.length - 1;
  const shouldShowSubmit =
    effectiveMode === 'single' || state.currentStep === visibleSteps.length - 1;

  // 🆕 Submit button text
  const finalSubmitText = React.useMemo(() => {
    if (submitButtonText) return submitButtonText;
    if (effectiveMode === 'single') return 'Submit';
    return 'Complete';
  }, [submitButtonText, effectiveMode]);

  // Auto-save function with retry logic
  const performAutoSave = React.useCallback(async () => {
    if (!onAutoSave || !formIsDirty) return;

    const currentData = { ...state.formData, ...getValues() };
    setSaveStatus('saving');
    setErrorMessage('');

    try {
      await onAutoSave(currentData);
      setSaveStatus('saved');
      setLastSaved(new Date());
      // Reset form to clear dirty state after successful auto-save
      reset(currentData as T);
      retryCountRef.current = 0;

      // Also persist to session storage
      if (persistData) {
        saveFormData(persistKey, currentData);
      }
    } catch (error) {
      const err = error as Error;
      setSaveStatus('error');
      setErrorMessage(err.message || 'Failed to save');

      // Retry logic with exponential backoff
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
        setTimeout(() => performAutoSave(), retryDelay);
      }
    }
  }, [onAutoSave, formIsDirty, state.formData, getValues, persistData, persistKey, reset]);

  // Set up auto-save interval
  React.useEffect(() => {
    if (autoSaveInterval > 0 && onAutoSave) {
      autoSaveTimerRef.current = setInterval(() => {
        performAutoSave();
      }, autoSaveInterval);

      return () => {
        if (autoSaveTimerRef.current) {
          clearInterval(autoSaveTimerRef.current);
        }
      };
    }
  }, [autoSaveInterval, onAutoSave, performAutoSave]);

  // Update form data and persist if enabled
  // Deep merge preserves arrays (like items) from previous steps when the current
  // step's getValues() returns default/empty arrays for fields not on this step.
  const updateFormData = React.useCallback(
    (newData: Partial<T>) => {
      setState((prev) => {
        const updatedData = { ...prev.formData };
        for (const key of Object.keys(newData) as Array<keyof T>) {
          const newVal = newData[key];
          const prevVal = prev.formData[key];
          // Preserve existing populated arrays when new value is empty (default from unrelated step)
          if (
            Array.isArray(prevVal) &&
            prevVal.length > 0 &&
            Array.isArray(newVal) &&
            newVal.length === 0
          ) {
            continue;
          }
          (updatedData as Record<string, unknown>)[key as string] = newVal;
        }

        // Persist data if enabled
        if (persistData) {
          saveFormData(persistKey, updatedData);
        }

        // Bug #49/#50: Sync ref immediately so goToStep (called in the same stack)
        // reads the freshly merged data instead of stale closure values.
        formDataRef.current = updatedData;

        return {
          ...prev,
          formData: updatedData,
        };
      });
    },
    [persistData, persistKey]
  );

  // Navigation functions
  const goToStep = React.useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < visibleSteps.length) {
        setState((prev) => ({
          ...prev,
          currentStep: stepIndex,
        }));

        // Bug #49/#50: Use formDataRef (not state.formData) to read the latest merged
        // data. When updateFormData + goToStep are called in sequence (e.g., goNext/goPrevious),
        // state.formData from the closure is stale — the ref is updated synchronously.
        reset(formDataRef.current as T);

        // Notify parent of step change
        onStepChange?.(stepIndex, visibleSteps[stepIndex].id);
      }
    },
    [visibleSteps, reset, onStepChange]
  );

  const goNext = React.useCallback(() => {
    if (state.currentStep < visibleSteps.length - 1) {
      // Validate current step before proceeding. The second arg is RHF's
      // `onInvalid` — without it, validation failures are silent (the click
      // is "received" but nothing happens), which is exactly the symptom
      // QA reported across every multi-step add form.
      //
      // Per-step Zod schemas strip fields outside the current step, so the
      // resolver's `data` alone loses cross-step state set via `setValue()`
      // (e.g. step 1's `selectedOrders`, step 3's `optimizedRoute`). Merge
      // raw RHF values first, then overlay `data` so this step's resolver
      // transforms (`z.coerce`, `.transform()`) still win.
      handleSubmit((data) => {
        updateFormData({ ...getValues(), ...data });
        setState((prev) => ({
          ...prev,
          completedSteps: new Set([...prev.completedSteps, prev.currentStep]),
        }));
        goToStep(state.currentStep + 1);
      }, handleValidationFailure)();
    }
  }, [
    state.currentStep,
    visibleSteps.length,
    handleSubmit,
    getValues,
    updateFormData,
    goToStep,
    handleValidationFailure,
  ]);

  const goPrevious = React.useCallback(() => {
    if (state.currentStep > 0) {
      // Save current data without validation
      const currentData = getValues();
      updateFormData(currentData);
      goToStep(state.currentStep - 1);
    }
  }, [state.currentStep, getValues, updateFormData, goToStep]);

  const submitForm = React.useCallback(() => {
    handleSubmit(async (data) => {
      // Final-step resolver strips non-step fields too — see `goNext`.
      const finalData = { ...state.formData, ...getValues(), ...data } as T;
      setIsSubmitting(true);

      try {
        await onSubmit(finalData);

        // Clear persisted data on successful submission
        if (persistData) {
          clearFormData(persistKey);
        }

        // Clear auto-save status
        setSaveStatus('saved');
        setLastSaved(new Date());
      } catch (error) {
        logger.error('Form submission failed', { error, persistKey });
        setSaveStatus('error');
        setErrorMessage('Failed to submit form');
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    }, handleValidationFailure)();
  }, [
    handleSubmit,
    getValues,
    state.formData,
    onSubmit,
    persistData,
    persistKey,
    handleValidationFailure,
  ]);

  // Context value
  const contextValue: MultiStepFormContextType<T> = {
    currentStep: state.currentStep,
    totalSteps: visibleSteps.length,
    isFirstStep: state.currentStep === 0,
    isLastStep: state.currentStep === visibleSteps.length - 1,
    canGoNext: isValid && state.currentStep < visibleSteps.length - 1,
    canGoPrevious: state.currentStep > 0,
    goToStep,
    goNext,
    goPrevious,
    getCurrentStepData: () => getValues(),
    getAllFormData: () => state.formData,
    updateFormData,
    submitForm,
  };

  // Update form when step changes
  React.useEffect(() => {
    reset(state.formData as T);
  }, [state.currentStep, state.formData, reset]);

  // Sync external `defaultValues` reference changes into the wizard's form
  // state. The reducer's initial-state factory (search for
  // `useState(() => ({ formData: initializeFormData()`) captures the initial
  // `defaultValues` ONCE, which means async parent data (e.g. a React-Query
  // fetch that resolves AFTER mount) was previously ignored — every
  // dependent field appeared "blank" in edit mode (B-007 P1: shipment edit
  // clears every field; root cause: `useOrder()` resolves after the
  // `useState` initial-state factory has already run).
  //
  // Strategy: detect ref change → if user hasn't started editing, re-init
  // and let the existing reset-on-formData effect (above) propagate to RHF.
  // Gating on `formIsDirty=false` prevents clobbering in-flight user edits
  // when the parent's data refetches mid-session.
  const prevDefaultValuesRef = React.useRef(defaultValues);
  React.useEffect(() => {
    if (prevDefaultValuesRef.current === defaultValues) return;
    prevDefaultValuesRef.current = defaultValues;
    if (formIsDirty) return;

    setState((prev) => ({ ...prev, formData: initializeFormData() }));
  }, [defaultValues, formIsDirty, initializeFormData]);

  const currentStepConfig = visibleSteps[state.currentStep];
  const progressPercentage =
    visibleSteps.length > 0 ? ((state.currentStep + 1) / visibleSteps.length) * 100 : 0;

  if (!currentStepConfig) {
    return (
      <Box padding={4}>
        <Text color="fg.error">No valid steps found</Text>
      </Box>
    );
  }

  const StepComponent = currentStepConfig.component;

  return (
    <MultiStepFormContext.Provider
      value={contextValue as MultiStepFormContextType<Record<string, unknown>>}
    >
      <FormProvider {...formMethods}>
        <Container maxWidth="4xl" className={className}>
          <VStack gap={6} align="stretch">
            {/* Save Indicator */}
            {showSaveIndicator && saveIndicatorPosition === 'top' && (
              <HStack justifyContent="flex-end">
                <SaveIndicator
                  status={saveStatus}
                  lastSaved={lastSaved}
                  errorMessage={errorMessage}
                  showTimestamp={true}
                />
              </HStack>
            )}

            {/* Validation Summary - Top */}
            {showValidationSummary &&
              validationSummaryPosition === 'top' &&
              getValidationErrors.length > 0 && (
                <ErrorSummary
                  errors={getValidationErrors}
                  title={validationSummaryTitle}
                  onFieldClick={handleFieldClick}
                />
              )}

            {/* Progress Indicator */}
            {shouldShowStepper && (
              <Box>
                <HStack justifyContent="space-between" marginBottom={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Step {state.currentStep + 1} of {visibleSteps.length}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {Math.round(progressPercentage)}% Complete
                  </Text>
                </HStack>
                <Progress.Root value={progressPercentage} size="sm">
                  <Progress.Track backgroundColor="bg.subtle" borderRadius="md">
                    <Progress.Range backgroundColor="blue.500" />
                  </Progress.Track>
                </Progress.Root>
              </Box>
            )}

            {/* Step Header */}
            <Box textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" marginBottom={2}>
                {currentStepConfig.title}
              </Text>
              {currentStepConfig.description && (
                <Text color="fg.muted" fontSize="md">
                  {currentStepConfig.description}
                </Text>
              )}
            </Box>

            {/* Step Content */}
            <Card.Root variant="default" size="lg">
              <Card.Body paddingY={8}>
                <StepComponent
                  formMethods={formMethods}
                  currentStepData={getValues()}
                  allFormData={state.formData}
                  isActive={true}
                  onNext={goNext}
                  onPrevious={goPrevious}
                />
              </Card.Body>
            </Card.Root>

            {/* 🆕 Navigation Buttons */}
            {showNavigationButtons && (
              <HStack justifyContent="space-between" gap={4}>
                {/* Left side - Previous/Cancel */}
                <HStack gap={2}>
                  {shouldShowPrevious && (
                    <Button variant="outline" onClick={goPrevious} disabled={isSubmitting}>
                      {previousButtonText}
                    </Button>
                  )}
                  {onCancel && (
                    <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
                      {cancelButtonText}
                    </Button>
                  )}
                </HStack>

                {/* Right side - Next/Submit */}
                <HStack gap={2}>
                  {shouldShowNext && (
                    <Button variant="primary" onClick={goNext} disabled={isSubmitting}>
                      {nextButtonText}
                    </Button>
                  )}
                  {shouldShowSubmit && (
                    <Button
                      variant="primary"
                      onClick={submitForm}
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                    >
                      {finalSubmitText}
                    </Button>
                  )}
                </HStack>
              </HStack>
            )}

            {/* Validation Summary - Bottom */}
            {showValidationSummary &&
              validationSummaryPosition === 'bottom' &&
              getValidationErrors.length > 0 && (
                <ErrorSummary
                  errors={getValidationErrors}
                  title={validationSummaryTitle}
                  onFieldClick={handleFieldClick}
                />
              )}

            {/* Save Indicator - Inline */}
            {showSaveIndicator && saveIndicatorPosition === 'inline' && (
              <HStack justifyContent="flex-end">
                <SaveIndicator
                  status={saveStatus}
                  lastSaved={lastSaved}
                  errorMessage={errorMessage}
                  showTimestamp={true}
                />
              </HStack>
            )}

            {/* Custom children (for additional content) */}
            {children}

            {/* Save Indicator - Bottom */}
            {showSaveIndicator && saveIndicatorPosition === 'bottom' && (
              <HStack justifyContent="flex-end">
                <SaveIndicator
                  status={saveStatus}
                  lastSaved={lastSaved}
                  errorMessage={errorMessage}
                  showTimestamp={true}
                />
              </HStack>
            )}
          </VStack>
        </Container>
      </FormProvider>
    </MultiStepFormContext.Provider>
  );
};

// Utility function to create step configuration
export const createStep = <T extends FieldValues = FieldValues>(
  config: StepConfig<T>
): StepConfig<T> => config;
